import _styled from "styled-components";
import React from "react";
import { MediaGridContentImageCard, type mediaGridContentImageCardFragment } from "~/features/blocks/inventory/media-grid/content-image-card";
import type { FragmentType } from "~/gql/runtime";
type CardData = FragmentType<typeof mediaGridContentImageCardFragment>;
export type RowLayout = "auto" | "featured-left" | "featured-right";
type Props = {
  cards: (CardData | null | undefined)[];
  layout?: RowLayout;
};
export type CardPlacement = {
  cardIndex: number;
  colSpan: 3 | 4 | 6 | 8 | 12;
  featured?: boolean;
};
type LayoutConfig = {
  placements: CardPlacement[];
  rowGrid?: boolean;
};
function resolveLayout(count: number, layout: RowLayout): LayoutConfig {
  if (count === 4) {
    return {
      placements: Array.from({
        length: 4
      }, (_, i) => ({
        cardIndex: i,
        colSpan: 3 as const
      }))
    };
  }
  if (count === 2 && layout === "featured-left") {
    return {
      placements: [{
        cardIndex: 0,
        colSpan: 8
      }, {
        cardIndex: 1,
        colSpan: 4
      }]
    };
  }
  if (count === 2 && layout === "featured-right") {
    return {
      placements: [{
        cardIndex: 0,
        colSpan: 4
      }, {
        cardIndex: 1,
        colSpan: 8
      }]
    };
  }
  if (count === 3 && layout === "featured-left") {
    return {
      rowGrid: true,
      placements: [{
        cardIndex: 0,
        colSpan: 6,
        featured: true
      }, {
        cardIndex: 1,
        colSpan: 6
      }, {
        cardIndex: 2,
        colSpan: 6
      }]
    };
  }
  if (count === 3 && layout === "featured-right") {
    return {
      rowGrid: true,
      placements: [{
        cardIndex: 0,
        colSpan: 6
      }, {
        cardIndex: 2,
        colSpan: 6,
        featured: true
      }, {
        cardIndex: 1,
        colSpan: 6
      }]
    };
  }
  const span = (count === 1 ? 12 : count === 2 ? 6 : 4) as 4 | 6 | 12;
  return {
    placements: Array.from({
      length: count
    }, (_, i) => ({
      cardIndex: i,
      colSpan: span
    }))
  };
}
export type MediaGridContentRowViewProps<TCard> = {
  cards: (TCard | null | undefined)[];
  getCardKey?: (card: TCard, placement: CardPlacement) => React.Key;
  layout?: RowLayout;
  renderCard: (card: TCard, placement: CardPlacement) => React.ReactNode;
};
export function MediaGridContentRowView<TCard>({
  cards,
  getCardKey,
  layout = "auto",
  renderCard
}: MediaGridContentRowViewProps<TCard>) {
  const validCards = cards?.filter((c): c is TCard => c != null) ?? [];
  if (!validCards.length) {
    return null;
  }
  const config = resolveLayout(validCards.length, layout);
  return <_StyledDiv $_css={[{
    "display": "grid",
    "width": "100%",
    "gap": "var(--spacing-md)"
  }, config.rowGrid ? {
    "gridTemplateColumns": "repeat(1, minmax(0, 1fr))",
    "@media (min-width: 1024px)": {
      "gridTemplateColumns": "repeat(12, minmax(0, 1fr))",
      "gridTemplateRows": "repeat(2, minmax(0, 1fr))"
    }
  } : validCards.length === 1 ? {
    "gridTemplateColumns": "repeat(1, minmax(0, 1fr))"
  } : {
    "gridTemplateColumns": "repeat(2, minmax(0, 1fr))",
    "@media (min-width: 1024px)": {
      "gridTemplateColumns": "repeat(12, minmax(0, 1fr))"
    }
  }]}>
      {config.placements.map(placement => {
      const card = validCards[placement.cardIndex];
      if (card == null) {
        return null;
      }
      return <React.Fragment key={getCardKey?.(card, placement) || placement.cardIndex}>
            {renderCard(card, placement)}
          </React.Fragment>;
    })}
    </_StyledDiv>;
}
export function MediaGridContentRow({
  cards,
  layout = "auto"
}: Props) {
  return <MediaGridContentRowView cards={cards} layout={layout} renderCard={(card, placement) => <MediaGridContentImageCard {...card} colSpan={placement.colSpan} featured={placement.featured} />} />;
}
var _StyledDiv = /*#__PURE__*/_styled("div").withConfig({
  displayName: "content-row___StyledDiv",
  componentId: "sc-kwnynq-0"
})(["", ""], p => p.$_css);